// SPDX-License-Identifier: Apache-2.0
import Collection from 'ol/Collection';
import Feature from 'ol/Feature';
import tippy, { Instance } from 'tippy.js';

import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { type Geometry } from 'ol/geom';
import { Fill, Icon, Stroke, Style } from 'ol/style';
import { containsExtent, type Extent, getCenter } from 'ol/extent';
import type { Color } from 'vanilla-picker';
import GirafeColorPicker from '../../tools/utils/girafecolorpicker';

import PinIcon from './images/pin.svg';
import LayerIcon from './images/layer.svg';
import LayerGroupIcon from './images/layergroup.svg';
import SearchIcon from './images/search.svg';
import PaintbrushIcon from './images/paintbrush.svg';

import GirafeHTMLElement from '../../base/GirafeHTMLElement';
import type { SearchResultsActions } from '../../models/searchresult';
import ThemeLayer from '../../models/layers/themelayer';
import { LayerTreeChanges } from '../../tools/themes/themeshelper';
import { UsedInTemplateOnly } from '../../decorators';
import { SEARCH_GROUP_DEFAULT, SEARCH_PROVIDER_DEFAULT } from '../../tools/search/abstractsearchclient';

class SearchComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = [
    '../../../node_modules/vanilla-picker/dist/vanilla-picker.csp.css',
    '../../styles/girafecolorpicker.css',
    '../../styles/common.css',
    './style.css'
  ];

  public searchIcon: string = SearchIcon;
  public paintbrushIcon: string = PaintbrushIcon;

  private get map() {
    return this.context.mapManager.getMap();
  }

  private readonly previewFeaturesCollection: Collection<Feature<Geometry>> = new Collection();
  private previewLayers?: LayerTreeChanges;
  private previewGeoLayer: VectorLayer<VectorSource> | null = null;

  private ignoreBlur = false;
  public groupedResults: Record<string, Record<string, Feature[]>> = {};
  protected allResults: Feature[] = [];
  protected forceHide = true;

  private focusedResultIndex = -1;
  private focusedResult: Feature | null = null;
  private selectedResult: Feature | null = null;

  private searchInput?: HTMLInputElement;
  private activeSearchFilter: { [provider: string]: string[] } | null = null;
  private girafeColorPicker?: GirafeColorPicker;

  public paintSearchResults?: boolean;
  public defaultSearchStrokeColor!: string;
  public defaultSearchFillColor!: string;
  public searchStrokeColor!: string;
  public searchFillColor!: string | number[];
  public showNoResultWarning = false;
  private tooltipInstance: Instance | null = null;

  // Keeping track of the last input timeout
  private ongoingSearchTimeoutId = 0;

  public constructor() {
    super('search');
  }

  private async initialSearch() {
    const searchTerm = this.context.permalinkManager.getSearchTerm();
    const results = await this.context.searchManager.doSearch(searchTerm);
    if (results !== null) {
      // Apply the first search result in the list
      const firstResults = this.flattenResultList(results);
      if (firstResults.length) {
        this.preview(firstResults[0]);
        this.onSelect(firstResults[0]);
      }
    }
  }

  private createPreviewLayer() {
    this.paintSearchResults = this.context.configManager.Config.search?.paintSearchResults;

    this.previewGeoLayer = new VectorLayer({
      properties: {
        addToPrintedLayers: true
      },
      source: new VectorSource({
        features: this.previewFeaturesCollection
      })
    });
    this.updatePreviewLayerStyle(this.defaultSearchFillColor, this.defaultSearchStrokeColor);
    this.map.addLayer(this.previewGeoLayer);
    this.previewGeoLayer.setZIndex(1010);

    this.initColorPicker();
  }

  toggleVisibility(visible: boolean) {
    (this.shadowRoot?.host as HTMLElement).style.display = visible ? 'flex' : 'none';
  }

  public onMouseDown() {
    this.ignoreBlur = true;
  }

  public onFocusIn() {
    this.forceHide = false;
    super.render();
  }

  public onFocusOut() {
    if (!this.ignoreBlur) {
      this.forceHide = true;
      super.render();
    }
    this.ignoreBlur = false;
  }

  override render() {
    super.render();
    this.searchInput = this.shadowRoot?.getElementById('search') as HTMLInputElement;
  }

  registerEvents(): void {
    this.subscribe('interface.searchComponentVisible', (_oldValue: boolean, newValue: boolean) =>
      this.toggleVisibility(newValue)
    );

    this.subscribe('interface.searchFilterComponentVisible', (_oldValue: boolean, isVisible: boolean) => {
      if (isVisible) {
        this.forceHide = true;
        this.refreshRender();
      } else {
        this.searchInput!.dispatchEvent(new Event('input'));
      }
    });

    this.subscribe('search.filter', (_oldValue, newValue) => {
      this.activeSearchFilter = newValue;
      // Remove any shortcuts from the search term as soon as the related filter is applied
      this.sanitizeSearchTerm(this.searchInput?.value ?? '');
      this.searchInput!.dispatchEvent(new Event('input'));
    });

    this.subscribe('language', () => {
      this.context.i18nManager.ensureTranslationLoaded().then(() => {
        this.tooltipInstance?.setContent(this.createToolTipContent());
      });
    });
  }

  protected override connectedCallback() {
    super.connectedCallback();

    this.defaultSearchStrokeColor = this.context.configManager.Config.search?.defaultStrokeColor as string;
    this.defaultSearchFillColor = this.context.configManager.Config.search?.defaultFillColor as string;
    this.createPreviewLayer();

    this.render();
    super.girafeTranslate();
    this.registerEvents();
    this.context.i18nManager.ensureTranslationLoaded().then((loaded) => {
      if (loaded) {
        this.createTooltip();
      } else {
        console.debug('Translations not loaded');
      }
    });
    if (this.context.permalinkManager.hasSearch()) {
      this.subscribe('application.isReady', () => {
        if (this.state.application.isReady) {
          void this.initialSearch();
        }
      });
    }
  }

  private flattenResultList(results?: Record<string, Record<string, Feature[]>>): Feature[] {
    results ??= this.groupedResults;
    return Object.values(results).flatMap((providerResults) => Object.values(providerResults).flat());
  }

  protected clearSearch(purge = false) {
    if (purge) {
      if (this.searchInput) {
        this.searchInput.value = '';
      }
    }
    this.forceHide = this.state.interface.searchFilterComponentVisible;
    this.groupedResults = {};
    this.allResults = [];
    this.clearPreview();
    this.focusedResultIndex = -1;
    this.focusedResult = null;
    this.closeToolTip();
    super.render();
  }

  public async doSearch(e: Event) {
    this.showNoResultWarning = false;

    this.context.searchManager.abortSearch();
    this.clearSearch();

    const target = e.target as HTMLInputElement;
    const term = this.processSearchFilterShortcut(target.value);

    if (term.trim().length > 0) {
      try {
        const features = await this.context.searchManager.doSearch(term, this.activeSearchFilter);

        // Check if a search was performed at all (min term length!)
        if (features !== null) {
          this.groupedResults = features;
          this.allResults = this.flattenResultList();

          if (!this.allResults.length) {
            // If the search did not yield any results, a warning
            // box is displayed for 2 seconds and then fades out (CSS)
            this.showNoResultWarning = true;
          }
        }

        // And then rerender the results
        super.render();
        super.girafeTranslate();
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          // Request was aborted, ignore the error
          console.debug('Multiple parallel search: previous request was aborted.');
          return;
        }
      }
    }
  }

  /**
   * Debounce the fetch call to API to prevent sending request at every stroke.
   * @param e
   */
  public async doSearchDebounced(e: Event) {
    if (this.ongoingSearchTimeoutId !== 0) {
      clearTimeout(this.ongoingSearchTimeoutId);
    }

    // The original even cannot be passed because event objects are ephemeral and
    // not designed to be used asynchronously (the target property would be undefined)
    const syntheticEvent = {
      target: { value: e.target && 'value' in e.target ? e.target.value : undefined }
    } as unknown as Event;

    this.ongoingSearchTimeoutId = window.setTimeout(() => {
      this.ongoingSearchTimeoutId = 0;
      this.doSearch(syntheticEvent);
    }, 300);
  }

  /**
   * Detects manually entered filter shortcuts and sanitizes the search term.
   */
  private processSearchFilterShortcut(term: string) {
    const shortcutDetected = this.context.searchManager.detectSearchFilterShortcut(term);
    if (shortcutDetected) {
      // If a complete shortcut was detected and saved to the state, sanitize the search term
      if (this.state.search.filterShortcut) {
        return this.sanitizeSearchTerm(term);
      } else {
        // No complete shortcut detected yet, wait for next input
        return '';
      }
    }
    return term;
  }

  private sanitizeSearchTerm(term: string) {
    const prefix = this.context.stateManager.state.search.filterShortcutPrefix ?? '';
    const shortcut = this.context.stateManager.state.search.filterShortcut ?? '';
    const sanitizedSearchTerm = term.replace(`${prefix}${shortcut}`, '').trimStart();
    this.searchInput!.value = sanitizedSearchTerm;
    this.searchInput!.focus();
    return sanitizedSearchTerm;
  }

  @UsedInTemplateOnly()
  public addFullThemeIfNotPresent(feature: Feature) {
    this.clearPreview();
    const action = this.getFeatureFirstAction(feature);
    if (!action) {
      return;
    }
    const layer = this.context.themesHelper.findLayerByName(action.data);
    if (!layer) {
      return;
    }
    const theme = this.context.themesHelper.getOriginalTheme(layer);
    if (!theme) {
      return;
    }
    const added = this.context.themesHelper.addThemeIfNotPresent(theme);
    if (!added) {
      // If it's not added because it's already in the tree, complete the theme.
      this.context.themesHelper.completeTheme(theme);
    }
    const insertedLayer = this.context.themesHelper.findBaseLayerRecursiveById(this.state.layers.layersList, layer.id);
    if (insertedLayer) {
      insertedLayer.activeState = 'on';
    }
    this.closeDropdown(feature);
    this.onFocusOut();
    this.updateSearchboxWithResult(feature);
  }

  @UsedInTemplateOnly()
  public getGroupIcon(searchGroup: string) {
    switch (searchGroup) {
      case 'Group':
        return LayerGroupIcon;
      case 'Layer':
        return LayerIcon;
      default:
        return PinIcon;
    }
  }

  @UsedInTemplateOnly()
  public getProviderClass(provider: string) {
    if (Object.keys(this.groupedResults).length === 1 || provider === SEARCH_PROVIDER_DEFAULT) {
      return 'hidden';
    }
    return 'provider entry';
  }

  @UsedInTemplateOnly()
  public getGroupClass(group: string) {
    if (group === SEARCH_GROUP_DEFAULT) {
      return 'hidden';
    }
    return 'group entry';
  }

  @UsedInTemplateOnly()
  public getLabel(result: Feature): unknown {
    return result.get('label_html') ? this.htmlUnsafe(result.get('label_html')) : result.get('label');
  }

  @UsedInTemplateOnly()
  public isFeatureActionThemeRelated(feature: Feature): boolean {
    const action = this.getFeatureFirstAction(feature);
    const kind = action?.action;
    const hasThemeRelatedAction =
      kind?.startsWith('add_layer') || kind?.startsWith('add_group') || kind?.startsWith('add_theme') || false;
    if (!hasThemeRelatedAction || !action) {
      return false;
    }
    const layer = this.context.themesHelper.findLayerByName(action.data);
    return !!layer;
  }

  public onMouseOver(result: Feature) {
    this.focusResult(result);
  }

  public onMouseLeave() {
    // Clear preview search result, only if the result was not selected
    if (this.selectedResult === null) {
      this.clearPreview();
    }
  }

  private focusResultFromIndex() {
    const result = this.allResults[this.focusedResultIndex];
    this.focusResult(result);
  }

  private focusResult(result: Feature) {
    // Clear old selection and preview
    this.clearPreview();
    if (this.focusedResult) {
      this.focusedResult.set('selected', false);
    }

    // Set the newly selected object and activate a preview.
    this.focusedResultIndex = this.allResults.indexOf(result);
    this.focusedResult = this.allResults[this.focusedResultIndex];
    this.focusedResult.set('selected', true);
    this.render();
    this.preview(result);

    // Scroll to selected div
    const resultHtmlElement = this.shadow.querySelectorAll('.result')[this.focusedResultIndex];
    resultHtmlElement.scrollIntoView({ block: 'nearest' });
  }

  private preview(result: Feature) {
    if (result.getGeometry() && this.context.configManager.Config.search?.objectPreview) {
      // Result with geometry
      this.addFeatureToPreview(result);
      this.updatePreviewLayerStyle();
    }
    const firstAction = this.getFeatureFirstAction(result);
    if (firstAction?.action.startsWith('add_layer') && this.context.configManager.Config.search?.layerPreview) {
      const layer = this.context.themesHelper.findLayerByName(firstAction.data);
      if (layer) {
        const clonedTheme = this.context.themesHelper.getMinimalClonedThemeForLayer(layer);
        clonedTheme.isExpanded = true;
        this.previewLayers = this.context.themesHelper.mergeThemeInLayerTree(clonedTheme, true);
      } else {
        console.error(`Layer ${firstAction.data} cannot be found`);
      }
    }
  }

  private addFeatureToPreview(feature: Feature) {
    this.previewFeaturesCollection.push(feature);
  }

  public clearPreview() {
    // Clear preview search result
    this.previewFeaturesCollection.clear();

    // Clear preview layer
    if (this.previewLayers) {
      this.context.themesHelper.removeLayersFromLayerTree(this.previewLayers.insertedLayers);
      for (const layer of this.previewLayers.activatedLayers) {
        this.context.layerManager.toggle(layer, 'off');
      }
      this.previewLayers = undefined;
    }
  }

  public onSelect(feature: Feature) {
    this.closeDropdown(feature);

    // If the result has a specific zoom-to extent defined or contains a geometry, zoom to it
    const zoomBbox = this.context.searchManager.getZoomToBbox(feature);
    if (zoomBbox) {
      this.zoomTo(zoomBbox);
    } else {
      this.addResultToTreeView(feature);
    }

    this.onFocusOut();
    this.updateSearchboxWithResult(feature);
  }

  private closeDropdown(feature: Feature | null) {
    this.selectedResult = feature;
    this.ignoreBlur = false;
    this.forceHide = true;
    this.previewLayers = undefined;
    super.render();
  }

  private updateSearchboxWithResult(feature: Feature) {
    if (this.searchInput && feature.get('label')) {
      this.searchInput.value = feature.get('label');
    }
  }

  private getFeatureFirstAction(feature: Feature): SearchResultsActions | undefined {
    const actions = feature.get('actions') as SearchResultsActions[] | undefined;
    return actions?.length ? actions[0] : undefined;
  }

  private addResultToTreeView(feature: Feature) {
    const action = this.getFeatureFirstAction(feature);
    if (!action) {
      // Nothing to add
      return;
    }

    let clonedTheme: ThemeLayer | undefined;
    let activate = false;
    if (action.action.startsWith('add_theme')) {
      const theme = this.context.themesHelper.findThemeByName(action.data);
      if (theme) {
        clonedTheme = theme.clone();
      }
    } else if (action.action.startsWith('add_group')) {
      const group = this.context.themesHelper.findGroupByName(action.data);
      if (group) {
        clonedTheme = this.context.themesHelper.getMinimalClonedThemeForLayer(group);
      }
    } else if (action.action.startsWith('add_layer')) {
      const layer = this.context.themesHelper.findLayerByName(action.data);
      if (layer) {
        clonedTheme = this.context.themesHelper.getMinimalClonedThemeForLayer(layer);
        activate = true;
      }
    } else {
      console.warn('Unsupported result type');
    }

    if (clonedTheme) {
      this.context.themesHelper.mergeThemeInLayerTree(clonedTheme, activate);
    }
  }

  private zoomTo(extent: Extent) {
    const minResolution = this.context.configManager.Config.search?.minResolution;
    const currentResolution = this.map.getView().getResolution() as number;
    const currentExtent = this.map.getView().calculateExtent();

    if (minResolution) {
      if (currentResolution > minResolution) {
        // If we are in a bigger resolution as the minimal one,
        // Zoom to object with minResolution
        this.context.mapManager.zoomToExtent(extent, minResolution);
      } else if (!containsExtent(currentExtent, extent)) {
        // Else, if the extent is NOT already within the current extent of the map
        // We keep the current resolution, and just pan to object
        this.state.position.center = getCenter(extent);
      }
    }
    // Otherwise, if the searched object is already in the current map extent
    // We do nothing
  }

  public onMouseMove() {
    // if the mouse moves, we activate the hover effect
    const results = this.shadowRoot?.querySelectorAll('.result');

    if (!results) {
      return;
    }

    for (const result of results) {
      result.classList.remove('active');
      const htmlResult = result as HTMLElement;
      htmlResult.style.removeProperty('background-color');
    }
  }

  public onKeyDown(e: KeyboardEvent) {
    // clear search on escape
    if (e.key === 'Escape') {
      this.clearSearch(true);
    }

    // automatic re-open search results on enter
    else if (this.forceHide) {
      if (e.key === 'Enter') {
        this.onFocusIn();
      }
    }

    // navigate through search results
    else if (!this.forceHide) {
      this.navigateToResult(e);
    }
  }

  private navigateToResult(e: KeyboardEvent) {
    switch (e.key) {
      case 'ArrowDown':
        if (this.focusedResultIndex < this.allResults.length - 1) {
          this.focusedResultIndex += 1;
          this.focusResultFromIndex();
        }
        break;

      case 'ArrowUp':
        if (this.focusedResultIndex > 0) {
          this.focusedResultIndex -= 1;
          this.focusResultFromIndex();
        }
        break;

      case 'Enter':
        if (this.focusedResultIndex >= 0) {
          this.onSelect(this.allResults[this.focusedResultIndex]);
        }
        break;
    }
  }

  private createToolTipContent(): HTMLElement {
    // Create a temporary div to hold the content
    const tooltipContent = document.createElement('div');
    // Custom tooltip: Check if there's a slot named "search-tooltip"
    const slot = this.shadow.querySelector('slot[name="search-tooltip"]') as HTMLSlotElement;
    if (slot) {
      const assignedNodes = slot.assignedNodes({ flatten: true });
      if (assignedNodes.length > 0) {
        assignedNodes.forEach((node) => {
          tooltipContent.appendChild(node.cloneNode(true));
        });
        return tooltipContent;
      }
    }
    // Default tooltip
    const tooltipI18nKey = 'search_tooltip';
    tooltipContent.innerHTML = this.context.i18nManager.getTranslation(tooltipI18nKey);
    // Add an explanation about filter shortcuts
    if (this.context.searchManager.hasFilterShortcutsEnabled()) {
      const shortcutPrefix = this.context.configManager.Config.search!.filterShortcutPrefix!;
      const prefixList = shortcutPrefix.map((prefix) => `"${prefix}"`).join(', ');
      const filterShortcutHelp = this.context.i18nManager
        .getTranslation('Type {prefix} to view available shortcuts to quickly filter search results.')
        .replace('{prefix}', prefixList);
      tooltipContent.innerHTML += `<br>${filterShortcutHelp}`;
    }
    return tooltipContent;
  }

  private createTooltip() {
    const searchBox = this.shadowRoot?.getElementById('searchbox');
    if (searchBox) {
      // Create a tooltip using tippy.js
      this.tooltipInstance = tippy(searchBox, {
        content: () => {
          return this.createToolTipContent();
        },
        trigger: 'mouseenter',
        hideOnClick: false,
        interactive: true,
        theme: 'light',
        placement: 'left',
        arrow: true,
        appendTo: document.body
      });
    }
  }

  private closeToolTip() {
    // Remove the tooltip to not cover any results
    const res = document.querySelector('[data-tippy-root]');
    if (res) {
      res.remove();
    }
  }

  private initColorPicker() {
    super.render();
    const colorPicker = this.shadowRoot?.getElementById('colorPickerBtn');
    if (colorPicker) {
      this.girafeColorPicker = new GirafeColorPicker({
        parent: colorPicker,
        color: this.context.configManager.Config.search?.defaultStrokeColor,
        popup: 'right'
      });
      this.girafeColorPicker.onChange = (color: Color) => {
        // The fill color should be the selected color with a bit more transparency
        const fillColor = [color.rgba[0], color.rgba[1], color.rgba[2], color.rgba[3] / 2];
        this.searchFillColor = fillColor;
        this.searchStrokeColor = color.hex;
        this.updatePreviewLayerStyle(fillColor, color.hex);
      };
      this.girafeColorPicker.onOpen = (_) => {
        if (this.defaultColorHasChanged()) {
          this.girafeColorPicker?.setColor(
            this.context.configManager.Config.search?.defaultStrokeColor as string,
            true
          );
        }
      };
    }
  }

  private updatePreviewLayerStyle(fillColor?: string | number[], strokeColor?: string) {
    if (!this.previewGeoLayer) {
      return;
    }

    // Only update style if new colors were provided via color picker or default colors have changed
    if (this.defaultColorHasChanged()) {
      const defaultFillColor = this.context.configManager.Config.search?.defaultFillColor as string;
      this.defaultSearchFillColor = defaultFillColor;
      this.searchFillColor = defaultFillColor;
      const defaultStrokeColor = this.context.configManager.Config.search?.defaultStrokeColor as string;
      this.defaultSearchStrokeColor = defaultStrokeColor;
      this.searchStrokeColor = defaultStrokeColor;
    }
    const strokeColorWithFallback = strokeColor ?? this.searchStrokeColor ?? this.defaultSearchStrokeColor;
    const fillColorWithFallback = fillColor ?? this.searchFillColor ?? this.defaultSearchFillColor;

    this.previewGeoLayer.setStyle(
      new Style({
        stroke: new Stroke({
          color: strokeColorWithFallback,
          width: this.context.configManager.Config.search?.defaultStrokeWidth
        }),
        fill: new Fill({ color: fillColorWithFallback }),
        image: new Icon({
          anchor: [0.5, 1],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          src: this.getColoredPinIcon(strokeColorWithFallback),
          scale: 0.3
        })
      })
    );
  }

  private defaultColorHasChanged(): boolean {
    return (
      this.defaultSearchFillColor !== this.context.configManager.Config.search?.defaultFillColor ||
      this.defaultSearchStrokeColor !== this.context.configManager.Config.search?.defaultStrokeColor
    );
  }

  private getColoredPinIcon(hexColor: string) {
    const pin = `<svg xmlns="http://www.w3.org/2000/svg" 
                      width="120" 
                      height="120" 
                      style="fill: ${hexColor};" 
                      viewBox="0 0 384 512">
                    <path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 
                             307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z"/>
                 </svg>`;

    return `data:image/svg+xml;utf8,${encodeURIComponent(pin)}`;
  }
}

export default SearchComponent;
